// Students Progress — step 3: one student's full progress within a course
// ("/app/students-progress/:courseId/:studentId"). Three tabs: Overall
// Summary (stat tiles + a combined progress-over-time line chart), and two
// independent sortable/filterable history tables (Daily Performance,
// Tests) — kept separate per module by design, matching the isolated
// backend data sources.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpDown, Search } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import dayjs from "dayjs";

const TABS = [
  { key: "summary", label: "Overall Summary" },
  { key: "daily-performance", label: "Daily Performance" },
  { key: "tests", label: "Tests" },
];

function StatTile({ label, value }) {
  return (
    <div className="border border-zinc-200 p-4">
      <div className="font-heading text-2xl font-black">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function HistoryTable({ rows, testId }) {
  const [sortKey, setSortKey] = useState("submitted_at");
  const [sortDir, setSortDir] = useState("desc");
  const [q, setQ] = useState("");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const filtered = rows.filter((r) =>
    !q.trim() || r.title.toLowerCase().includes(q.toLowerCase()) || (r.subject || "").toLowerCase().includes(q.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
    return sortDir === "asc" ? cmp : -cmp;
  });

  const Th = ({ children, sortField }) => (
    <th className="px-5 py-3 font-semibold cursor-pointer select-none" onClick={() => toggleSort(sortField)}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className="w-3 h-3 text-zinc-400" /></span>
    </th>
  );

  return (
    <div className="space-y-3" data-testid={`history-table-${testId}`}>
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
        <input
          data-testid={`history-filter-${testId}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by test name or subject…"
          className="w-full border border-zinc-300 pl-8 pr-3 py-2 text-sm"
        />
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-zinc-500 py-6">No attempts found.</p>
      ) : (
        <div className="border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-[0.1em] text-zinc-500">
              <tr>
                <Th sortField="title">Test Name</Th>
                <Th sortField="subject">Subject</Th>
                <Th sortField="submitted_at">Date</Th>
                <Th sortField="score">Score</Th>
                <Th sortField="total">Max Score</Th>
                <Th sortField="pct">Percentage</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.test_id} className="border-t border-zinc-100">
                  <td className="px-5 py-3 font-medium">{r.title}</td>
                  <td className="px-5 py-3 text-zinc-500">{r.subject || "—"}</td>
                  <td className="px-5 py-3 text-zinc-500">{dayjs(r.submitted_at).format("D MMM YYYY, h:mm A")}</td>
                  <td className="px-5 py-3 font-semibold text-blue-700">{r.score}</td>
                  <td className="px-5 py-3 text-zinc-500">{r.total}</td>
                  <td className="px-5 py-3 font-semibold">{r.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function StudentProgressDetail() {
  const { user } = useAuth();
  const { courseId, studentId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("summary");

  useEffect(() => {
    if (user?.role !== "teacher" && user?.role !== "admin") return;
    api.get(`/teacher/students-progress/${courseId}/${studentId}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)));
  }, [courseId, studentId, user]);

  if (user?.role !== "teacher" && user?.role !== "admin") {
    return <p className="text-sm text-red-600" data-testid="student-progress-detail-forbidden">This page is for teachers only.</p>;
  }
  if (!data) return <p className="text-sm text-zinc-500" data-testid="student-progress-detail-loading">Loading…</p>;

  const pct = (v) => (v == null ? "—" : `${v}%`);

  const trend = [...data.dp_history, ...data.test_history]
    .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
    .map((r, i) => ({
      name: dayjs(r.submitted_at).format("D MMM"),
      idx: i,
      ...(data.dp_history.includes(r) ? { "Daily Performance %": r.pct } : { "Test %": r.pct }),
    }));

  return (
    <div className="space-y-6" data-testid="student-progress-detail-page">
      <Link to={`/app/students-progress/${courseId}`} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950">
        <ArrowLeft className="w-4 h-4" /> Back to ranked list
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-700">{data.course_title}</p>
        <h1 className="font-heading text-3xl font-black tracking-tight mt-1" data-testid="student-progress-detail-name">{data.student_name}</h1>
        <p className="text-sm text-zinc-500 mt-1">{data.student_email}</p>
      </div>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`student-progress-tab-${t.key}`}
            className={`px-5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-blue-700 text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile label="Rank" value={data.rank ?? "Unranked"} />
            <StatTile label="Overall Avg" value={pct(data.overall_avg)} />
            <StatTile label="Test Avg" value={pct(data.test_avg_pct)} />
            <StatTile label="Daily Performance Avg" value={pct(data.dp_avg_pct)} />
            <StatTile label="Total Tests Attempted" value={data.test_history.length} />
            <StatTile label="Total Daily Performance Attempted" value={data.dp_history.length} />
            <StatTile label="Attendance %" value={pct(data.attendance_pct)} />
          </div>

          <div className="border border-zinc-200 p-5">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-4">Progress over time</p>
            {trend.length === 0 ? (
              <p className="text-sm text-zinc-400 py-8">No attempts yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} height={44} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Test %" stroke="#1d4ed8" connectNulls strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Daily Performance %" stroke="#e63946" connectNulls strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {tab === "daily-performance" && <HistoryTable rows={data.dp_history} testId="dp" />}
      {tab === "tests" && <HistoryTable rows={data.test_history} testId="tests" />}
    </div>
  );
}
